/**
 * useRegulatoryOnboarding — State machine for regulatory edge suggestions (DRR-075).
 *
 * Two-phase suggestion strategy:
 * 1. Deterministic match (instant): NAICS → industry → regulatory-nodes applies_to_industries
 * 2. LLM enrichment (async, optional): Bedrock suggests confidence scores + reasons
 *
 * Builds live ConstellationData from accepted suggestions for real-time
 * constellation preview in the wizard step.
 *
 * On wizard submit, seeds accepted edges via addScopeEdge mutations.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
// eslint-disable-next-line no-restricted-imports -- Data-layer hook, not UI component
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';

import type { ConstellationData, RegulatoryNode } from '@ferp/workflow-forms/scope';

// Jurisdiction type from useRegulatoryConstellation (not re-exported from scope barrel
// to avoid conflict with Jurisdiction interface in @ferp/workflow-forms types.ts)
type Jurisdiction = 'federal' | 'state' | 'payer' | 'standard';

import {
  REGULATORY_NODES,
  UNIVERSAL_EMPLOYER_NODES,
  HEALTHCARE_UNIVERSAL_NODES,
  getIndustriesForNaics,
  type RegulatoryNodeData,
} from '../data/regulatoryNodes';

// ---------------------------------------------------------------------------
// GraphQL Operations
// ---------------------------------------------------------------------------

const SUGGEST_REGULATORY_NODES = gql`
  mutation SuggestRegulatoryNodes($input: SuggestRegulatoryNodesInput!) {
    suggestRegulatoryNodes(input: $input) {
      suggestions {
        nodeId
        name
        jurisdiction
        edgeType
        confidence
        reason
        priority
        conditional
        notes
      }
      processingTimeMs
    }
  }
`;

// ---------------------------------------------------------------------------
// Mutation result type (not generated — inline gql document)
// ---------------------------------------------------------------------------

interface SuggestRegulatoryNodesMutationData {
  suggestRegulatoryNodes: {
    suggestions: Array<Record<string, unknown>>;
    processingTimeMs: number;
  };
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RegulatoryNodeSuggestion {
  /** Matches regulatory-nodes config node_id. */
  readonly nodeId: string;
  /** Human-readable name. */
  readonly name: string;
  /** Jurisdiction level. */
  readonly jurisdiction: Jurisdiction;
  /** Edge type: REGULATED_BY (mandatory) or GOVERNED_BY (voluntary). */
  readonly edgeType: 'REGULATED_BY' | 'GOVERNED_BY';
  /** Confidence score 0-1. */
  readonly confidence: number;
  /** Human-readable reason. */
  readonly reason: string;
  /** Primary regulatory body. */
  readonly priority?: 'primary';
  /** Conditional applicability. */
  readonly conditional?: boolean;
  /** Additional notes. */
  readonly notes?: string;
  /** Where this suggestion came from. */
  readonly source: 'deterministic' | 'llm';
}

export type SuggestionStatus = 'pending' | 'accepted' | 'rejected';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface RegulatoryOnboardingState {
  /** All suggestions keyed by nodeId. */
  readonly suggestions: readonly RegulatoryNodeSuggestion[];
  /** Status of each suggestion by nodeId. */
  readonly statuses: Record<string, SuggestionStatus>;
  /** Whether deterministic matching has completed. */
  readonly hasMatched: boolean;
  /** Whether LLM enrichment is in progress. */
  readonly isLlmEnriching: boolean;
  /** Whether seeding is in progress. */
  readonly isSeeding: boolean;
  /** Seeding progress. */
  readonly seedProgress: { readonly seeded: number; readonly total: number };
  /** Error message. */
  readonly error: string | null;
}

const INITIAL_STATE: RegulatoryOnboardingState = {
  suggestions: [],
  statuses: {},
  hasMatched: false,
  isLlmEnriching: false,
  isSeeding: false,
  seedProgress: { seeded: 0, total: 0 },
  error: null,
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

type Action =
  | { type: 'MATCH_COMPLETE'; suggestions: RegulatoryNodeSuggestion[] }
  | { type: 'LLM_START' }
  | { type: 'LLM_COMPLETE'; enriched: RegulatoryNodeSuggestion[] }
  | { type: 'ACCEPT_NODE'; nodeId: string }
  | { type: 'REJECT_NODE'; nodeId: string }
  | { type: 'UNDO_NODE'; nodeId: string }
  | { type: 'ACCEPT_ALL_HIGH_CONFIDENCE' }
  | { type: 'RESET_ALL' }
  | { type: 'SEED_START'; total: number }
  | { type: 'SEED_EDGE_COMPLETE' }
  | { type: 'SEED_DONE' }
  | { type: 'SET_ERROR'; error: string };

function reducer(
  state: RegulatoryOnboardingState,
  action: Action,
): RegulatoryOnboardingState {
  switch (action.type) {
    case 'MATCH_COMPLETE': {
      const statuses: Record<string, SuggestionStatus> = {};
      for (const s of action.suggestions) {
        statuses[s.nodeId] = 'pending';
      }
      return {
        ...state,
        suggestions: action.suggestions,
        statuses,
        hasMatched: true,
        error: null,
      };
    }

    case 'LLM_START':
      return { ...state, isLlmEnriching: true, error: null };

    case 'LLM_COMPLETE': {
      // Merge LLM suggestions: update existing or add new
      const existingMap = new Map(
        state.suggestions.map((s) => [s.nodeId, s]),
      );
      const newStatuses = { ...state.statuses };

      for (const enriched of action.enriched) {
        if (existingMap.has(enriched.nodeId)) {
          // Update confidence/reason from LLM
          existingMap.set(enriched.nodeId, {
            ...existingMap.get(enriched.nodeId)!,
            confidence: enriched.confidence,
            reason: enriched.reason,
            priority: enriched.priority,
            conditional: enriched.conditional,
            notes: enriched.notes,
            source: 'llm',
          });
        } else {
          // New suggestion from LLM
          existingMap.set(enriched.nodeId, enriched);
          newStatuses[enriched.nodeId] = 'pending';
        }
      }

      return {
        ...state,
        suggestions: Array.from(existingMap.values()),
        statuses: newStatuses,
        isLlmEnriching: false,
      };
    }

    case 'ACCEPT_NODE':
      return {
        ...state,
        statuses: { ...state.statuses, [action.nodeId]: 'accepted' },
      };

    case 'REJECT_NODE':
      return {
        ...state,
        statuses: { ...state.statuses, [action.nodeId]: 'rejected' },
      };

    case 'UNDO_NODE':
      return {
        ...state,
        statuses: { ...state.statuses, [action.nodeId]: 'pending' },
      };

    case 'ACCEPT_ALL_HIGH_CONFIDENCE': {
      const updated = { ...state.statuses };
      for (const s of state.suggestions) {
        if (s.confidence >= 0.8 && updated[s.nodeId] === 'pending') {
          updated[s.nodeId] = 'accepted';
        }
      }
      return { ...state, statuses: updated };
    }

    case 'RESET_ALL': {
      const reset: Record<string, SuggestionStatus> = {};
      for (const s of state.suggestions) {
        reset[s.nodeId] = 'pending';
      }
      return { ...state, statuses: reset };
    }

    case 'SEED_START':
      return {
        ...state,
        isSeeding: true,
        seedProgress: { seeded: 0, total: action.total },
        error: null,
      };

    case 'SEED_EDGE_COMPLETE':
      return {
        ...state,
        seedProgress: {
          ...state.seedProgress,
          seeded: state.seedProgress.seeded + 1,
        },
      };

    case 'SEED_DONE':
      return { ...state, isSeeding: false };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.error,
        isLlmEnriching: false,
        isSeeding: false,
      };

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Deterministic Matching
// ---------------------------------------------------------------------------

/**
 * Generate suggestions by matching NAICS industry keywords against
 * regulatory node `applies_to_industries` fields.
 */
function matchRegulatoryNodes(
  naicsSector: string | undefined,
  naicsSubsector: string | undefined,
  stateCode: string | undefined,
): RegulatoryNodeSuggestion[] {
  if (!naicsSector) return [];

  const industries = getIndustriesForNaics(naicsSector, naicsSubsector);
  const industrySet = new Set(industries);
  const isHealthcare = naicsSector === '62';
  const suggestions: RegulatoryNodeSuggestion[] = [];

  for (const node of REGULATORY_NODES) {
    // Skip state-level nodes that don't match the tenant's state
    if (node.state && stateCode && node.state !== stateCode) {
      continue;
    }

    // Skip state-level nodes if no state selected yet
    if (node.state && !stateCode) {
      continue;
    }

    // Check industry match
    const hasIndustryMatch = node.appliesToIndustries.some((ind) =>
      industrySet.has(ind),
    );
    const isUniversalEmployer = UNIVERSAL_EMPLOYER_NODES.has(node.nodeId);
    const isHealthcareUniversal =
      isHealthcare && HEALTHCARE_UNIVERSAL_NODES.has(node.nodeId);

    if (!hasIndustryMatch && !isUniversalEmployer && !isHealthcareUniversal) {
      continue;
    }

    // Assign confidence
    let confidence: number;
    if (hasIndustryMatch) {
      confidence = 1;
    } else if (isHealthcareUniversal) {
      confidence = 0.95;
    } else {
      confidence = 0.7; // Universal employer nodes
    }

    suggestions.push({
      nodeId: node.nodeId,
      name: node.name,
      jurisdiction: node.jurisdiction,
      edgeType: node.defaultEdgeType,
      confidence,
      reason: node.defaultReason,
      source: 'deterministic',
    });
  }

  // Sort: highest confidence first, then by jurisdiction weight
  const jurisdictionWeight: Record<string, number> = {
    federal: 4,
    state: 3,
    payer: 2,
    standard: 1,
  };

  return suggestions.sort((a, b) => {
    if (b.confidence !== a.confidence) return b.confidence - a.confidence;
    return (
      (jurisdictionWeight[b.jurisdiction] || 0) -
      (jurisdictionWeight[a.jurisdiction] || 0)
    );
  });
}

// ---------------------------------------------------------------------------
// Constellation Builder
// ---------------------------------------------------------------------------

/**
 * Build ConstellationData from accepted suggestions for live preview.
 */
function buildConstellation(
  tenantId: string,
  tenantName: string,
  accepted: RegulatoryNodeSuggestion[],
): ConstellationData | null {
  if (accepted.length === 0) return null;

  const nodes: RegulatoryNode[] = accepted.map((s) => ({
    nodeId: s.nodeId,
    name: s.name,
    jurisdiction: s.jurisdiction,
    edgeType: s.edgeType,
    reason: s.reason,
    priority: s.priority,
    conditional: s.conditional,
    notes: s.notes,
  }));

  return {
    tenantId,
    tenantName,
    nodes,
    federalCount: nodes.filter((n) => n.jurisdiction === 'federal').length,
    stateCount: nodes.filter((n) => n.jurisdiction === 'state').length,
    payerCount: nodes.filter((n) => n.jurisdiction === 'payer').length,
    standardCount: nodes.filter((n) => n.jurisdiction === 'standard').length,
    regulatedByCount: nodes.filter((n) => n.edgeType === 'REGULATED_BY').length,
    governedByCount: nodes.filter((n) => n.edgeType === 'GOVERNED_BY').length,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseRegulatoryOnboardingProps {
  /** NAICS sector code (e.g., "62"). */
  readonly naicsSector?: string;
  /** NAICS subsector code (e.g., "621"). */
  readonly naicsSubsector?: string;
  /** US state code (e.g., "MN"). */
  readonly state?: string;
  /** Tenant ID for seeding. */
  readonly tenantId: string;
  /** Tenant display name for constellation center. */
  readonly tenantName: string;
}

export interface UseRegulatoryOnboardingReturn {
  /** All suggestions. */
  readonly suggestions: readonly RegulatoryNodeSuggestion[];
  /** Accepted suggestions. */
  readonly accepted: readonly RegulatoryNodeSuggestion[];
  /** Rejected suggestions. */
  readonly rejected: readonly RegulatoryNodeSuggestion[];
  /** Pending suggestions (not yet acted on). */
  readonly pending: readonly RegulatoryNodeSuggestion[];
  /** Status map for each nodeId. */
  readonly statuses: Record<string, SuggestionStatus>;
  /** Constellation data built from accepted nodes. */
  readonly constellation: ConstellationData | null;
  /** Whether deterministic matching has run. */
  readonly hasMatched: boolean;
  /** Whether LLM enrichment is in progress. */
  readonly isLlmEnriching: boolean;
  /** Error message. */
  readonly error: string | null;
  /** Accept a suggestion. */
  readonly acceptNode: (nodeId: string) => void;
  /** Reject a suggestion. */
  readonly rejectNode: (nodeId: string) => void;
  /** Return a suggestion to pending. */
  readonly undoNode: (nodeId: string) => void;
  /** Accept all suggestions with confidence >= 0.8. */
  readonly acceptAllHighConfidence: () => void;
  /** Reset all suggestions to pending. */
  readonly resetAll: () => void;
  /** Trigger LLM enrichment (updates confidence/reasons). */
  readonly enhanceWithLlm: () => Promise<void>;
  /** Whether seeding is in progress. */
  readonly isSeeding: boolean;
  /** Seeding progress tracker. */
  readonly seedProgress: { readonly seeded: number; readonly total: number };
}

export function useRegulatoryOnboarding(
  props: UseRegulatoryOnboardingProps,
): UseRegulatoryOnboardingReturn {
  const { naicsSector, naicsSubsector, state: stateCode, tenantId, tenantName } = props;
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [executeSuggest] = useMutation<SuggestRegulatoryNodesMutationData>(SUGGEST_REGULATORY_NODES);
  const propsRef = useRef(props);
  propsRef.current = props;

  // ---- Deterministic matching on input change ----------------------------
  useEffect(() => {
    if (!naicsSector) return;
    const suggestions = matchRegulatoryNodes(naicsSector, naicsSubsector, stateCode);
    dispatch({ type: 'MATCH_COMPLETE', suggestions });
  }, [naicsSector, naicsSubsector, stateCode]);

  // ---- Derived lists from statuses ---------------------------------------
  const accepted = useMemo(
    () => state.suggestions.filter((s) => state.statuses[s.nodeId] === 'accepted'),
    [state.suggestions, state.statuses],
  );

  const rejected = useMemo(
    () => state.suggestions.filter((s) => state.statuses[s.nodeId] === 'rejected'),
    [state.suggestions, state.statuses],
  );

  const pending = useMemo(
    () => state.suggestions.filter((s) => state.statuses[s.nodeId] === 'pending'),
    [state.suggestions, state.statuses],
  );

  // ---- Live constellation from accepted ----------------------------------
  const constellation = useMemo(
    () => buildConstellation(tenantId, tenantName, accepted),
    [tenantId, tenantName, accepted],
  );

  // ---- Actions -----------------------------------------------------------
  const acceptNode = useCallback(
    (nodeId: string) => dispatch({ type: 'ACCEPT_NODE', nodeId }),
    [],
  );

  const rejectNode = useCallback(
    (nodeId: string) => dispatch({ type: 'REJECT_NODE', nodeId }),
    [],
  );

  const undoNode = useCallback(
    (nodeId: string) => dispatch({ type: 'UNDO_NODE', nodeId }),
    [],
  );

  const acceptAllHighConfidence = useCallback(
    () => dispatch({ type: 'ACCEPT_ALL_HIGH_CONFIDENCE' }),
    [],
  );

  const resetAll = useCallback(
    () => dispatch({ type: 'RESET_ALL' }),
    [],
  );

  // ---- LLM Enrichment via suggestRegulatoryNodes mutation ----------------
  const enhanceWithLlm = useCallback(async () => {
    const { naicsSector: sector, naicsSubsector: sub, state: st } = propsRef.current;
    if (!sector || !st) return;

    dispatch({ type: 'LLM_START' });

    try {
      const { data } = await executeSuggest({
        variables: {
          input: {
            naicsCode: sector,
            state: st,
            subsector: sub ?? undefined,
          },
        },
      });

      const result = data?.suggestRegulatoryNodes;
      if (!result?.suggestions) {
        dispatch({ type: 'LLM_COMPLETE', enriched: [] });
        return;
      }

      const enriched: RegulatoryNodeSuggestion[] = result.suggestions.map(
        (s: Record<string, unknown>) => ({
          nodeId: s.nodeId as string,
          name: s.name as string,
          jurisdiction: s.jurisdiction as Jurisdiction,
          edgeType: s.edgeType as 'REGULATED_BY' | 'GOVERNED_BY',
          confidence: s.confidence as number,
          reason: s.reason as string,
          priority: s.priority as 'primary' | undefined,
          conditional: (s.conditional as boolean) ?? false,
          notes: s.notes as string | undefined,
          source: 'llm' as const,
        }),
      );

      dispatch({ type: 'LLM_COMPLETE', enriched });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'LLM enrichment failed';
      dispatch({ type: 'SET_ERROR', error: message });
    }
  }, [executeSuggest]);

  return {
    suggestions: state.suggestions,
    accepted,
    rejected,
    pending,
    statuses: state.statuses,
    constellation,
    hasMatched: state.hasMatched,
    isLlmEnriching: state.isLlmEnriching,
    error: state.error,
    acceptNode,
    rejectNode,
    undoNode,
    acceptAllHighConfidence,
    resetAll,
    enhanceWithLlm,
    isSeeding: state.isSeeding,
    seedProgress: state.seedProgress,
  };
}
